import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template172: Golden Gate - bloques separados (hero, intro, goldenGate, palace, crissyField, llegar)
const Template172 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const contentColor = textColor || "#444";

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const getCanvasImageUrl = (id) =>
		page.canvasImages?.find((img) => img.id === id)?.imageUrl ?? "";

	const headerText = page.texts?.[0]?.content ?? "";
	const introText = page.texts?.[1]?.content ?? "";
	const logoImage = getCanvasImageUrl("logo");
	const hero = page?.hero ?? page?.data?.hero ?? page?.data?.data?.hero ?? {};
	const heroImageSrc = getImageSrc(hero.imageId);
	const heroStickerSrc = getImageSrc(hero.stickerImageId);
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const goldenGate = sections[0] ?? {};
	const palace = sections[1] ?? {};
	const crissyField = sections[2] ?? {};
	const llegar = page?.llegar ??
		page?.data?.llegar ??
		page?.data?.data?.llegar ?? { title: "", items: [] };

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#F2B705",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: "'Poppins', sans-serif",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{heroImageSrc && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					<Box
						component="img"
						src={heroImageSrc}
						alt="Golden Gate"
						sx={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{hero.title && (
						<Box
							component="h1"
							sx={{
								fontFamily: "'Poppins', sans-serif",
								fontSize: 32,
								textAlign: "center",
								position: "absolute",
								bottom: 0,
								margin: "10px",
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{hero.title}
						</Box>
					)}
					{heroStickerSrc && (
						<Box
							component="img"
							src={heroStickerSrc}
							alt="GoldenGate"
							sx={{ width: 90, position: "absolute", bottom: 50, left: 25 }}
						/>
					)}
				</Box>
			)}

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px", position: "relative" }}>
					<Box
						component="p"
						sx={{
							fontFamily: "'Poppins', sans-serif",
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				sx={{
					padding: "0 12px",
					display: "flex",
					flexDirection: "column",
					gap: 1.5,
					marginTop: 1,
				}}
			>
				{/* ——— Golden Gate Bridge ——— */}
				<Box data-block-id="text-2" sx={{ display: "flex", gap: 2, position: "relative" }}>
					{(getImageSrc(goldenGate.imageId) ||
						getImageSrc(goldenGate.tooltipImageId)) && (
						<Box
							sx={{ display: "flex", flexDirection: "column", width: "40%" }}
						>
							{getImageSrc(goldenGate.imageId) && (
								<Box
									component="img"
									src={getImageSrc(goldenGate.imageId)}
									alt=""
									sx={{
										width: "100%",
										height: 100,
										borderRadius: "12px",
										objectFit: "cover",
									}}
								/>
							)}
							{(goldenGate.tooltipText ||
								getImageSrc(goldenGate.tooltipImageId)) && (
								<Box sx={{ position: "relative" }}>
									{goldenGate.tooltipText && (
										<Box
											sx={{
												position: "absolute",
												bottom: isSmallScreen ? -80 : -50,
												left: isSmallScreen ? 8 : 60,
												backgroundColor: "#fff8db",
												border: "2px solid #f5c400",
												borderRadius: "16px 16px 16px 0px",
												padding: "10px 12px",
												fontSize: 11,
												fontFamily: "'Poppins', sans-serif",
												color: contentColor,
												boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
												maxWidth: 120,
												zIndex: 6,
											}}
											dangerouslySetInnerHTML={{
												__html: goldenGate.tooltipText,
											}}
										/>
									)}
									{getImageSrc(goldenGate.tooltipImageId) && (
										<Box
											component="img"
											src={getImageSrc(goldenGate.tooltipImageId)}
											alt=""
											sx={{
												width: 100,
												position: "absolute",
												left: -30,
												bottom: isSmallScreen ? -140 : -80,
												zIndex: 5,
											}}
										/>
									)}
								</Box>
							)}
						</Box>
					)}
					{goldenGate.sabiasQue?.[2] && (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: isSmallScreen ? "32%" : "34%",
								zIndex: 1000,
							}}
						>
							<SabiasQue
								icono={goldenGate.sabiasQue[2].icono}
								texto={goldenGate.sabiasQue[2].texto}
							/>
						</Box>
					)}
					{goldenGate.sabiasQue?.[0] && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? 52 : 40,
								left: isSmallScreen ? "32%" : "34%",
								zIndex: 30,
							}}
						>
							<SabiasQue
								icono={goldenGate.sabiasQue[0].icono}
								texto={goldenGate.sabiasQue[0].texto}
							/>
						</Box>
					)}
					{goldenGate.sabiasQue?.[1] && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? 12 : 0,
								left: isSmallScreen ? "32%" : "34%",
								zIndex: 20,
							}}
						>
							<SabiasQue
								icono={goldenGate.sabiasQue[1].icono}
								texto={goldenGate.sabiasQue[1].texto}
							/>
						</Box>
					)}
					<Box sx={{ width: "60%", display: "flex", flexDirection: "column" }}>
						{goldenGate.title && (
							<Box>
								<Box component="h3" sx={h3}>
									{goldenGate.title}
								</Box>
								{goldenGate.body && (
									<Box component="p" sx={p}>
										{goldenGate.body}
									</Box>
								)}
							</Box>
						)}
						{goldenGate.consejo && (
							<Box
								sx={{
									backgroundColor: "#fbf0cf",
									border: "3px solid #F2B705",
									borderRadius: "12px",
									fontFamily: "'Poppins', sans-serif",
									width: "100%",
									padding: "4px 8px",
									marginTop: 1,
								}}
							>
								<Box component="p" sx={{ ...p, margin: 0, fontSize: 11 }}>
									{goldenGate.consejo}
								</Box>
							</Box>
						)}
					</Box>
				</Box>

				{/* ——— Palace of Fine Arts ——— */}
				<Box
					data-block-id="text-3"
					sx={{
						backgroundColor: "#fbf0cf",
						padding: "4px",
						borderRadius: "12px",
						display: "flex",
						gap: 2,
						alignItems: "center",
						position: "relative",
						marginTop: isSmallScreen ? 4 : 0,
					}}
				>
					<Box sx={{ paddingLeft: 1 }}>
						{palace.title && (
							<Box component="h3" sx={h3}>
								{palace.title}
							</Box>
						)}
						{palace.body && (
							<Box component="p" sx={p}>
								{palace.body}
							</Box>
						)}
					</Box>
					{palace.imageId && getImageSrc(palace.imageId) && (
						<Box
							component="img"
							src={getImageSrc(palace.imageId)}
							alt=""
							sx={{ width: 100 }}
						/>
					)}
					{palace.sabiasQue?.[0] && (
						<Box
							sx={{
								position: "absolute",
								top: isSmallScreen ? -40 : -12,
								right: isSmallScreen ? "50%" : "50%",
								zIndex: 10,
							}}
						>
							<SabiasQue
								icono={palace.sabiasQue[0].icono}
								texto={palace.sabiasQue[0].texto}
							/>
						</Box>
					)}
				</Box>

				{/* ——— Crissy Field + Cómo llegar ——— */}
				<Box
					data-block-id="text-4"
					sx={{
						display: "flex",
						gap: isSmallScreen ? 1 : 2.5,
						flexDirection: isSmallScreen ? "column" : "row",
					}}
				>
					<Box
						sx={{
							display: "flex",
							width: isSmallScreen ? "100%" : "40%",
							gap: 2,
						}}
					>
						<Box sx={{ position: "relative" }}>
							{crissyField.title && (
								<Box component="h3" sx={h3}>
									{crissyField.title}
								</Box>
							)}
							{crissyField.body && (
								<Box component="p" sx={p}>
									{crissyField.body}
								</Box>
							)}
						</Box>
					</Box>
					{llegar.title && (
						<Box
							data-block-id="text-5"
							sx={{
								backgroundColor: "#fbf0cf",
								border: "3px solid #F2B705",
								borderRadius: "12px",
								padding: "8px 20px",
								fontSize: 12,
								fontFamily: "'Poppins', sans-serif",
								color: "#333",
								marginBottom: isSmallScreen ? 1.5 : 0,
								width: isSmallScreen ? "100%" : "60%",
							}}
						>
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: 1,
									fontSize: 13,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									gap: 0.75,
									color: "#ca9904",
									textTransform: "uppercase",
								}}
							>
								{llegar.title}
							</Box>
							<Box
								component="ul"
								sx={{ paddingLeft: 2.5, margin: 0, lineHeight: 1.5 }}
							>
								{llegar.items?.map((item, i) => (
									<li
										key={i}
										dangerouslySetInnerHTML={{
											__html:
												(item.label ? `<strong>${item.label}</strong>: ` : "") +
												(item.text ?? ""),
										}}
									/>
								))}
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template172.thumbnail = "/images/template-1.png";
export default Template172;
